import { useEffect, useState } from 'react'

import { Browser } from '@twistopayments/design-system/components/atoms/browser'
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'

import { BaseApp } from '@twisto/components/organisms/base-app'
import { HeadTags } from '@twisto/components/organisms/head-tags'
import { PageVisitAnalytics } from '@twisto/components/organisms/page-visit-analytics'
import { parseLocale } from '@twisto/nextjs/locale'

import { loadMessages } from 'flow/load-messages'

import { FlowClientProvider, FlowHandler, FlowLayout } from '../components'
import { flowPageVisitConfig } from '../components/debug-info/page-visit-analytics.config'
import { RiskHeadersProvider } from '../components/risk-headers-provider'

type AppPropsWithLayout = AppProps & {
  Component: NextPage
}

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
  const { locale, isReady, query } = useRouter()
  const [routerIsReady, setRouterIsReady] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { region, lang } = parseLocale(locale as any)

  useEffect(() => {
    if (isReady) {
      setRouterIsReady(true)
    }
  }, [isReady])

  // if it runs on the server
  if (!routerIsReady) {
    return <HeadTags lang={lang} region={region} />
  }

  return (
    <>
      <HeadTags lang={lang} region={region} />
      <Browser>
        <BaseApp loadMessages={loadMessages}>
          <PageVisitAnalytics
            config={flowPageVisitConfig(query.sessionId as string)}
          />
          <RiskHeadersProvider>
            <FlowClientProvider>
              <FlowLayout>
                <FlowHandler>
                  <Component {...pageProps} />
                </FlowHandler>
              </FlowLayout>
            </FlowClientProvider>
          </RiskHeadersProvider>
        </BaseApp>
      </Browser>
    </>
  )
}

export default App
